import { toast } from "sonner"
import { X } from "lucide-react"
import React from "react"
import { isWebBuild } from "@ideoz/core"

export interface ToastOptions {
  message: string
  info?: string
  link?: string
  linkText?: string
  type?: "cancel" | "errorAI" | "wait" | "responsive" | "error" | "thankYou" | "sorry" | "success"
  severity?: "success" | "error" | "warning" | "info"
  actionText?: string
  onAction?: () => void
  position?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right"
}
  const isWeb = isWebBuild();
// Helper function to show toast with custom content
// The styling and icons are handled by the Toaster component configuration
const chrSize = isWeb ? 6.3 : 8;
const textCharSize = isWeb ? 6.8 : 4;
export const showToast = (options: ToastOptions) => {
  console.log('showToast called with options:', options);
  
  const { message, info, link, linkText, type, severity, actionText, onAction, position } = options

  // Calculate dynamic width based on content
  const messageLength = message.length
  const infoLength = info ? info.length : 0
  const actionButtonWidth = actionText ? actionText.length * chrSize + 32 : 0 // ~8px per char + padding
  const linkWidth = linkText ? linkText.length * chrSize + 50 : 0 // ~7px per char for links
  
  // Base calculation: ~7px per character + base padding + buttons
  const textWidth = (messageLength + infoLength) * textCharSize
  const totalContentWidth = textWidth + actionButtonWidth + linkWidth +  (isWeb? 80 : 50) // +50 for close button + gaps + padding
  // Ensure minimum and maximum widths
  const calculatedWidth = Math.max(350, Math.min(totalContentWidth, isWeb? 900 : 650)) // Ensure at least 350px, max 700px
  const dynamicWidthClass = `min-w-[${calculatedWidth}px]`
  
  console.log('Width calculation:', {
    messageLength,
    infoLength,
    actionButtonWidth,
    textWidth,
    totalContentWidth,
    calculatedWidth,
    dynamicWidthClass
  });

  // Create custom toast content with layout: message → action button → close button
  const content = (
    <div style={{ width: `${calculatedWidth - 46}px` }}  className={`flex items-center justify-between gap-3 min-w-[300px]`}>
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-sm font-medium">
            {message}
          </span>
          {info && (
            <span className="text-sm font-medium">
              - {info}
            </span>
          )}
            {link && linkText && (
          <a 
            href={link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white ml-4 hover:text-blue-200 hover:underline text-sm"
          >
            {linkText}
          </a>
        )}
        </div>
      
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {actionText && onAction && (
          <button
            onClick={()=>{onAction(); toast.dismiss()}}
            className="text-white hover:text-gray-300 text-sm font-medium px-2 py-1 transition-colors"
          >
            {actionText}
          </button>
        )}
        <button
          onClick={() => toast.dismiss()}
          className="text-white hover:text-gray-300 transition-colors"
          aria-label="Close notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
  
  console.log('Toast content created with custom JSX');

  // Create toast options with dynamic width using inline styles
  const toastOptions = {
    style: {
      width: `${calculatedWidth}px`,
      maxWidth: `${calculatedWidth}px`,
      minWidth: `${calculatedWidth}px`,
    },
    classNames: {
      toast: `group toast bg-gray-900 text-white border-primary-800 shadow-lg rounded-lg px-4 py-3`,
    },
    position: position || undefined,
  }

  // Determine the toast method to use based on severity or type
  const toastSeverity = severity || (type === "success" || type === "cancel" ? "success" : 
                                   type === "error" || type === "errorAI" || type === "sorry" ? "error" :
                                   type === "wait" ? "info" : "info")
  console.log('Toast severity determined:', toastSeverity);

  // Use the appropriate toast method with custom content and options
  switch (toastSeverity) {
    case "success":
      console.log('Calling toast.success with custom content and options:', toastOptions);
      toast.success(content, toastOptions)
      break
    case "error":
      console.log('Calling toast.error with custom content and options:', toastOptions);
      toast.error(content, toastOptions)
      break
    case "warning":
      console.log('Calling toast.warning with custom content and options:', toastOptions);
      toast.warning(content, toastOptions)
      break
    case "info":
    default:
      console.log('Calling toast.info with custom content and options:', toastOptions);
      toast.info(content, toastOptions)
      break
  }
  console.log('Toast function completed');
}

// Convenience methods for common toast types
export const toastSuccess = (
  message: string, 
  info?: string, 
  actionText?: string, 
  onAction?: () => void
) => showToast({ message, info, severity: "success", actionText, onAction })

export const toastError = (
  message: string, 
  info?: string, 
  actionText?: string, 
  onAction?: () => void
) => showToast({ message, info, severity: "error", actionText, onAction })

export const toastWarning = (
  message: string, 
  info?: string, 
  actionText?: string, 
  onAction?: () => void
) => showToast({ message, info, severity: "warning", actionText, onAction })

export const toastInfo = (
  message: string, 
  info?: string, 
  actionText?: string, 
  onAction?: () => void
) => showToast({ message, info, severity: "info", actionText, onAction })
